/**
 * One size for the whole in-game interface. The automatic size follows the screen: 100% at 1080p, gently more on larger screens
 * (about 120% at 1440p and 150% at 4K) because those screens are also physically larger. The player's UI scale multiplies it and is
 * kept on this device. The CSS reads the result as --ui-zoom.
 */
export const UI_SCALE = { min: 80, max: 150, step: 5, default: 100 } as const;
const STORAGE_KEY = 'nimblez.ui-scale';

/** The screen's automatic size; the narrower of height and width decides, so ultrawide and 16:10 screens behave. */
export function automaticUiZoom(width: number, height: number) {
  const ratio = Math.min(height / 1080, width / 1920);
  return Math.min(2.2, Math.max(0.8, ratio ** 0.63));
}

/** The party column, the command bar and the combat log share the bottom edge; at 1080p sizes they need about this much width. */
const BOTTOM_ROW_WIDTH = 1400;

export function uiZoom(width: number, height: number, percent: number) {
  const zoom = Math.min(automaticUiZoom(width, height) * clampScale(percent) / 100, Math.max(0.8, width / BOTTOM_ROW_WIDTH));
  return Math.round(zoom * 1000) / 1000;
}

export function clampScale(percent: number) {
  if (!Number.isFinite(percent)) return UI_SCALE.default;
  const stepped = Math.round(percent / UI_SCALE.step) * UI_SCALE.step;
  return Math.min(UI_SCALE.max, Math.max(UI_SCALE.min, stepped));
}

function storedScale() {
  try { return clampScale(Number(localStorage.getItem(STORAGE_KEY) ?? UI_SCALE.default)); } catch { return UI_SCALE.default; }
}
function storeScale(percent: number) {
  try { localStorage.setItem(STORAGE_KEY, String(percent)); } catch { /* Private windows may refuse storage; the scale still applies. */ }
}

let percent: number | null = null;
let apply = () => {};

/** The player's interface size, as the settings slider reads and writes it. */
export const interfaceScale = {
  get percent() { return percent ??= storedScale(); },
  set(value: number) { percent = clampScale(value); storeScale(percent); apply(); },
};

/** Applies the scale now and on every resize. */
export function mountUiScale(root = document.documentElement) {
  apply = () => root.style.setProperty('--ui-zoom', String(uiZoom(window.innerWidth, window.innerHeight, interfaceScale.percent)));
  window.addEventListener('resize', apply);
  apply();
  return () => window.removeEventListener('resize', apply);
}
